import React, { useMemo } from "react";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material/styles";
import { searchCep } from "../../extras";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";
import RoomIcon from "@mui/icons-material/Room";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function AddressModal({
  show,
  closeModal,
  cep,
  setCep,
  street,
  setStreet,
  houseNumber,
  setHouseNumber,
  neighborhood,
  setNeighborhood,
  city,
  setCity,
  uf,
  setUf,
  complement,
  setComplement,
  reference,
  setReference,
  handleConfirmModal,
  deliveryCenterId,
  onConfirm,
}) {
  const theme = useTheme();

  const handleSearchCep = async () => {
    try {
      const data = await searchCep(cep);

      setStreet(data.logradouro);
      setNeighborhood(data.bairro);
      setCity(data.localidade);
      setUf(data.uf);
    } catch (e) {
      console.error(e);
    }
  };

  const isFormValid = useMemo(() => {
    return !!(cep && street && houseNumber && neighborhood && city && uf);
  }, [cep, street, houseNumber, neighborhood, city, uf]);

  const handleSubmit = () => {
    if (deliveryCenterId && handleConfirmModal) {
      handleConfirmModal(1);
    } else if (onConfirm) {
      onConfirm();
    } else {
      closeModal();
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="50%"
      maxWidth="700px"
      maxHeight="85vh"
    >
      <ModalHeader title="Endereço" icon={RoomIcon} onClose={closeModal} />
      <ModalContent padding={2}>
        <Box
          sx={{
            mt: 1,
            mb: 2,
            p: 1.5,
            borderRadius: "10px",
            bgcolor: alpha(theme.palette.info.main, 0.06),
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: theme.palette.info.main, flexShrink: 0 }}
          />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Dados usados apenas internamente para calcular rotas e integrações
            logísticas.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              id="cep"
              size="small"
              label="CEP"
              onBlur={handleSearchCep}
              variant="filled"
              fullWidth
              value={cep}
              onChange={(e) => setCep(e.target.value)}
              error={cep !== undefined && cep !== null && cep.length > 0 && cep.length < 8}
              helperText={
                cep && cep.length > 0 && cep.length < 8
                  ? "CEP inválido"
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              id="street"
              size="small"
              label="Rua"
              variant="filled"
              fullWidth
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              id="houseNumber"
              size="small"
              label="Número"
              variant="filled"
              fullWidth
              value={houseNumber}
              onChange={(e) => setHouseNumber(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              id="neighborhood"
              size="small"
              label="Bairro"
              variant="filled"
              fullWidth
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              id="city"
              size="small"
              label="Cidade"
              variant="filled"
              fullWidth
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              id="uf"
              size="small"
              label="Estado"
              variant="filled"
              fullWidth
              value={uf}
              onChange={(e) => setUf(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              id="complement"
              size="small"
              label="Complemento"
              variant="filled"
              fullWidth
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              id="reference"
              size="small"
              label="Referência"
              variant="filled"
              fullWidth
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Grid>
        </Grid>
      </ModalContent>

      <ModalFooter>
        <Button
          variant="outlined"
          onClick={closeModal}
          sx={{
            mr: 1.5,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            borderColor: theme.palette.grey[300],
            "&:hover": {
              borderColor: theme.palette.grey[400],
              bgcolor: theme.palette.grey[50],
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!isFormValid}
          onClick={handleSubmit}
          startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            bgcolor: theme.palette.primary.main,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&.Mui-disabled": {
              bgcolor: theme.palette.grey[200],
              color: theme.palette.grey[400],
            },
          }}
        >
          {deliveryCenterId ? "Alterar" : "Confirmar"}
        </Button>
      </ModalFooter>
    </AnimatedModal>
  );
}
